import React, { useState, useEffect } from 'react';
import { Job, JobStatus } from './Interfaces';
import NewJobModal from './NewJobModal';
import { collection, deleteDoc, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase'; 
import JobEditModal from './JobEditModal';
import TaskModal from './TaskModal';
import JobWorkspaceModal from './JobWorkspaceModal';
import JobStatusService from '../../utils/jobStatusService';


const JobSummary: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filterStatus, setFilterStatus] = useState('All'); // New state variable for filter
  const [searchTerm, setSearchTerm] = useState(''); // New state variable for search term
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(8);
  const [showJobEditModal, setShowJobEditModal] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false); // Add isTaskModalOpen state variable
  const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false); // New workspace modal state
  

  // Get current jobs
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = jobs.slice(0, jobsPerPage);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  
  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  
// Edit Job Modal
const handleOpenJobDetailModal = (job: Job) => {
  setSelectedJob(job);
  setShowJobEditModal(true);
};

const handleJobClick = (job: Job) => {
  setSelectedJob(job);
  setIsWorkspaceModalOpen(true); // Open the new workspace modal
};

const handleTaskModalClose = () => {
  setIsTaskModalOpen(false);
};

const handleWorkspaceModalClose = () => {
  setIsWorkspaceModalOpen(false);
};

  const getBorderColor= (status: string) => {
    switch (status) {
      case 'Open':
        return 'red';
      case 'Closed':
        return '#50CD89';
      case 'Pending':
        return 'orange';
      default:
        return ''; // Default border color
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => { // New handler for search change
    setSearchTerm(e.target.value);
  };

  const handleUpdateJobStatus = async (jobId: string, newStatus: JobStatus) => {
    try {
      const result = await JobStatusService.updateJobStatus(
        jobId, 
        newStatus, 
        'user', 
        'Manual status change from job summary'
      );
      
      if (result.success) {
        console.log("Job status updated successfully:", jobId);
      } else {
        console.error("Failed to update job status:", result.error);
      }
    } catch (error) {
      console.error("Error updating job status:", error);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => { // New handler for filter change
    setFilterStatus(e.target.value);
  };

  useEffect(() => {
    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(
      collection(db, 'jobs'),
      (querySnapshot) => {
        let updatedJobs = querySnapshot.docs.map(doc => {
          const docData = doc.data() as Job;
          return {
            ...docData,
            id: doc.id
          };
        });
  
        // Filter jobs by status if filter is not 'All'
        if (filterStatus !== 'All') {
          updatedJobs = updatedJobs.filter(job => job.status === filterStatus);
        }
  
        // Sort jobs by status and lastEdited
        updatedJobs.sort((a, b) => {
          const statusOrder = ['Open', 'Pending', 'Closed', 'Archived'];
          const statusDiff = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
          if (statusDiff !== 0) return statusDiff;
  
          // If statuses are the same, sort by lastEdited
          const aLastEdited = new Date(a.lastEdited).getTime();
          const bLastEdited = new Date(b.lastEdited).getTime();
          return bLastEdited - aLastEdited; // Descending order
        });
  
        setJobs(updatedJobs);
      },
      (error) => {
        console.error("Error fetching real-time updates:", error);
      }
    );
  
   // Clean up the subscription
   return () => {
    unsubscribe();
  };
}, [filterStatus]); // Add filterStatus as a dependency

  // Handle deep-link: open job by query param (e.g., ?jobId=...&view=task|edit)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const jobId = params.get('jobId');
      const view = params.get('view');
      if (!jobId) return;
      const job = jobs.find(j => j.id === jobId);
      if (!job) return;
      if (view === 'task') {
        setSelectedJob(job);
        setIsTaskModalOpen(true);
      } else if (view === 'edit') {
        setSelectedJob(job);
        setShowJobEditModal(true);
      }
    } catch {}
  }, [jobs]);

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

   // Function to handle job deletion
   const handleDeleteJob = async (jobId: string) => {
    try {
      await deleteDoc(doc(db, 'jobs', jobId));
      console.log("Job deleted successfully:", jobId);
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  return (
    <div className="container mt-3">
      <div className="card">
        {/* Card Header */}
        <div className="card-header d-flex justify-content-between align-items-center" style={{ overflow: 'visible' }}>
  <h1 className="card-title fs-3"><b>Job Tracking Summary</b></h1>
  <div className="d-flex" style={{ overflow: 'visible' }}>
    <input
      type="text"
      className="form-control me-2"
      value={searchTerm}
      onChange={handleSearchChange}
      placeholder="Search jobs..."
    />
    <select 
      className="form-select" 
      value={filterStatus} 
      onChange={handleFilterChange}
      style={{ position: 'relative', zIndex: 1000 }}
    >
      <option value="All">All</option>
      <option value="Open">Open</option>
      <option value="Closed">Closed</option>
      <option value="Pending">Pending</option>
      <option value="Archived">Archived</option>
    </select>
    <button className="btn btn-primary" onClick={handleOpenModal} style={{ minWidth: '100px', marginLeft: '20px' }}>New Job</button>
  </div>
</div>


        {/* Card Body */}
        <div className="card-body">
          <table className="table table-hover">
            <thead>
            <tr>
                <th className="text-center fs-6"><b>Creation Date</b></th>
                <th className="text-center fs-6"><b>Job Label</b></th>
                <th className="text-center fs-6"><b>Status</b></th>
                <th className="text-center fs-6"><b>Actions</b></th>
              </tr>
            </thead>
            <tbody>
  {currentJobs.filter(job => job.jobLabel.toLowerCase().includes(searchTerm.toLowerCase())).map((job, index) => (
    <tr key={index} onClick={() => handleJobClick(job)}>
      <td className="text-center">{job.creationDate}</td>
      <td className="text-center">{job.jobLabel}</td>
      <td className="text-center d-flex align-items-center justify-content-center">
        <select
          className="form-select"
          style={{
            width: '70%',
            fontSize: '0.8rem',
            borderColor: JobStatusService.getStatusColor(job.status) // Use service for colors
          }}
          value={job.status}
          onChange={(e) => {
            // Update the status in the local state
            const updatedJobs = [...jobs];
            updatedJobs[index].status = e.target.value as JobStatus;
            setJobs(updatedJobs);
          }}
          onBlur={() => handleUpdateJobStatus(job.id, job.status)}
          onClick={(e) => e.stopPropagation()} // Stop propagation of the click event
        >
          <option value="Pending">Pending Start</option>
          <option value="Open">Ready to Start</option>
          <option value="Awaiting Client Response">Awaiting Response</option>
          <option value="Overdue">Overdue</option>
          <option value="Closed">Completed</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Archived">Archived</option>
        </select>
      </td>
      <td className="text-center">
        {/* Open Edit Job Modal */}
        <button className="btn btn-link p-0 me-2" onClick={(event) => {
          event.stopPropagation();
          handleOpenJobDetailModal(job);
        }}>
          <i className="bi bi-pencil-square fs-3"></i>
        </button>

        {/* Delete Button */}
        <button className="btn btn-link p-0" onClick={(event) => {
          event.stopPropagation();
          if (window.confirm("Are you sure you want to delete this job?")) {
            handleDeleteJob(job.id);
          }
        }}>
          <i className="bi bi-trash fs-3"></i>
        </button>
      </td>
    </tr>
  ))}
</tbody>
          </table>
          {/* Legacy TaskModal - kept for backwards compatibility */}
{selectedJob && isTaskModalOpen && (
  <TaskModal 
    key={selectedJob.id} 
    show={isTaskModalOpen} 
    job={selectedJob} 
    onHide={handleTaskModalClose} 
  />
)}

{/* New Job Workspace Modal - Chat-centric collaboration */}
{selectedJob && isWorkspaceModalOpen && (
  <JobWorkspaceModal
    key={`workspace-${selectedJob.id}`}
    show={isWorkspaceModalOpen}
    job={selectedJob}
    onHide={handleWorkspaceModalClose}
  />
)}
        </div>
        <div className="d-flex justify-content-center pb-6">
  <button className="btn btn-primary w-15" onClick={() => setJobsPerPage(jobsPerPage + 8)}>Show More</button>
</div>
        {/* Card Footer */}
        <div className="card-footer">
          
          {/* Footer content goes here - e.g., buttons */}
        </div>
      </div>
      
      {/* NewJobModal */}
      <NewJobModal show={showModal} onClose={handleCloseModal} />
      {
  selectedJob && 
  <JobEditModal 
    key={selectedJob.id} 
    show={showJobEditModal} 
    job={selectedJob} 
    onHide={() => setShowJobEditModal(false)} 
  />
  
}

    </div>
  );
};

export default JobSummary;
